import type { ComponentPropsWithoutRef, ReactElement, ReactNode } from 'react';
import { Children, Fragment, useMemo } from 'react';

import { Icon } from '~/chords/Icon';
import { cn } from '~/chords/lib/cn';

export interface BreadcrumbsRootProps
  extends Omit<ComponentPropsWithoutRef<'nav'>, 'className'> {
  children: NonNullable<ReactNode>;
}

export function BreadcrumbsRoot({
  children,
  ...delegatedProps
}: BreadcrumbsRootProps) {
  const childrenWithSeparators = useMemo(() => {
    const validChildren = Children.toArray(children).filter(
      Boolean,
    ) as ReactElement[];

    return validChildren.map((child, index) => {
      return (
        <Fragment key={child.key}>
          {index ? (
            <Icon
              type="chevron-right"
              size="xs"
              role="presentation"
              aria-hidden
            />
          ) : null}
          {child}
        </Fragment>
      );
    });
  }, [children]);

  if (!childrenWithSeparators?.length) {
    return null;
  }

  return (
    <nav
      {...delegatedProps}
      className={cn(
        'flex min-h-[1.5rem] items-center justify-start space-x-sm self-stretch text-body-sm text-subtle',
      )}
    >
      {childrenWithSeparators}
    </nav>
  );
}

BreadcrumbsRoot.displayName = 'Breadcrumbs.Root';
